'use client';

import React, { isValidElement } from 'react';
import { type ReactNode, type Ref, createContext, useContext } from 'react';
import { HelpCircle } from '@untitledui/icons';
import type { InputProps as AriaInputProps, TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from 'react-aria-components';
import { HintText } from '@milanote/roma/components/base/input/hint-text';
import { Label } from '@milanote/roma/components/base/input/label';
import { Tooltip, TooltipTrigger } from '@milanote/roma/components/base/tooltip/tooltip';
import { cx, sortCx } from '@milanote/roma/utils/cx';
import { isReactComponent } from '@milanote/roma/utils/is-react-component';

const SEMANTIC_NAME_BASE = 'RomaInputBase';
const SEMANTIC_NAME_TEXT_FIELD = 'RomaTextField';
const SEMANTIC_NAME = 'RomaInput';

const TextFieldContext = createContext<TextFieldProps>({});

export interface InputBaseProps extends TextFieldProps {
    /**
     * Input size.
     * @default "sm"
     */
    size?: 'sm' | 'md';
    /** Placeholder text. */
    placeholder?: string;
    /** Class name for the input. */
    inputClassName?: string;
    /** Class name for the input wrapper. */
    wrapperClassName?: string;
    /** Class name for the tooltip. */
    tooltipClassName?: string;
    /** Keyboard shortcut to display. */
    shortcut?: string | boolean;
    groupRef?: Ref<HTMLDivElement>;
    /** Icon component or element to display on the left side of the input. */
    iconLeading?: React.FC<{ className?: string }> | ReactNode;
    /**
     * Icon component or element to display on the right side of the input.
     * Note: Takes precedence over `tooltip` when both are provided.
     */
    iconTrailing?: React.FC<{ className?: string }> | ReactNode;
    /**
     * Tooltip message on hover.
     * Note: Will not be displayed if `iconTrailing` is provided, as `iconTrailing` takes precedence.
     */
    tooltip?: string;
}

export const InputBase = React.forwardRef<HTMLInputElement, Omit<InputBaseProps, 'label' | 'hint'>>(
    (
        {
            tooltip,
            shortcut,
            groupRef,
            size = 'sm',
            isInvalid,
            isDisabled,
            iconLeading: LeadingIcon,
            iconTrailing: TrailingIcon,
            placeholder,
            wrapperClassName,
            tooltipClassName,
            inputClassName,
            // Omit this prop to avoid invalid HTML attribute warning
            isRequired: _isRequired,
            ...inputProps
        },
        ref,
    ) => {
        // Check if the input has trailing or leading icons
        const hasTrailingIcon = !!TrailingIcon || !!tooltip;
        const hasLeadingIcon = !!LeadingIcon;

        // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
        const context = useContext(TextFieldContext);

        const inputSize = context?.size || size;

        const sizes = sortCx({
            sm: {
                root: cx('px-3 py-1.5 text-sm', hasTrailingIcon && 'pr-9', hasLeadingIcon && 'pl-10'),
                iconLeading: 'left-3',
                iconTrailing: 'right-3',
                shortcut: 'pr-2.5',
            },
            md: {
                root: cx('px-3.5 py-2.5 text-md', hasTrailingIcon && 'pr-9.5', hasLeadingIcon && 'pl-10.5'),
                iconLeading: 'left-3.5',
                iconTrailing: 'right-3.5',
                shortcut: 'pr-3',
            },
        });

        return (
            <AriaGroup
                {...{ isDisabled, isInvalid }}
                ref={groupRef}
                className={({ isFocusWithin, isDisabled, isInvalid }) =>
                    cx(
                        SEMANTIC_NAME_BASE,
                        'relative flex w-full flex-row place-content-center place-items-center rounded-xs bg-primary ring-1 ring-primary duration-100 ease-linear ring-inset',

                        // Only show focus ring when input itself is focused, not when child button is focused
                        'has-[:focus]:ring-2 has-[:focus]:ring-primary_focused has-[button:focus]:ring-1 has-[button:focus]:ring-primary',

                        // Disabled state styles
                        isDisabled && 'cursor-not-allowed bg-disabled_subtle ring-disabled',
                        'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

                        // Invalid state styles
                        isInvalid && 'ring-error_subtle',
                        'group-invalid:ring-error_subtle',

                        // Invalid state with focus-within styles
                        isInvalid &&
                            isFocusWithin &&
                            'has-[:focus]:ring-2 has-[:focus]:ring-error has-[button:focus]:ring-1 has-[button:focus]:ring-error_subtle',

                        context?.wrapperClassName,
                        wrapperClassName,
                    )
                }
            >
                {/* Leading icon */}
                {isValidElement(LeadingIcon) && LeadingIcon}
                {isReactComponent(LeadingIcon) && (
                    <LeadingIcon
                        className={cx(
                            'absolute size-5 text-fg-quaternary',
                            isDisabled && 'text-fg-disabled',
                            sizes[inputSize].iconLeading,
                        )}
                    />
                )}

                {/* Input field */}
                <AriaInput
                    {...(inputProps as AriaInputProps)}
                    ref={ref}
                    placeholder={placeholder}
                    className={cx(
                        'm-0 w-full bg-transparent text-primary ring-0 outline-hidden placeholder:text-placeholder',
                        // autofill in chrome / firefox
                        'autofill:rounded-xs autofill:text-primary autofill:m-[2px]',
                        // disabled state
                        isDisabled && 'cursor-not-allowed text-disabled overflow-ellipsis',
                        'group-disabled:cursor-not-allowed group-disabled:text-disabled group-disabled:overflow-ellipsis',
                        // input size styles
                        sizes[inputSize].root,
                        context?.inputClassName,
                        inputClassName,
                    )}
                />

                {/* Trailing icon - takes precedence over tooltip */}
                {isValidElement(TrailingIcon) && TrailingIcon}
                {isReactComponent(TrailingIcon) && (
                    <TrailingIcon
                        className={cx(
                            'absolute size-5 text-fg-quaternary',
                            isDisabled && 'text-fg-disabled',
                            sizes[inputSize].iconTrailing,
                        )}
                    />
                )}

                {/* Tooltip and help icon - only shown if no trailing icon is provided */}
                {tooltip && !TrailingIcon && (
                    <Tooltip title={tooltip} placement="top" arrow>
                        <TooltipTrigger
                            className={cx(
                                'absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover',
                                sizes[inputSize].iconTrailing,
                                context?.tooltipClassName,
                                tooltipClassName,
                            )}
                        >
                            <HelpCircle className="size-4" />
                        </TooltipTrigger>
                    </Tooltip>
                )}

                {/* Shortcut */}
                {shortcut && (
                    <div
                        className={cx(
                            'pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8',
                            sizes[inputSize].shortcut,
                        )}
                    >
                        <span
                            className={cx(
                                'pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset',
                                isDisabled && 'bg-transparent text-disabled',
                            )}
                            aria-hidden="true"
                        >
                            {typeof shortcut === 'string' ? shortcut : '⌘K'}
                        </span>
                    </div>
                )}
            </AriaGroup>
        );
    },
);

InputBase.displayName = SEMANTIC_NAME_BASE;

interface BaseProps {
    /** Label text for the input */
    label?: string;
    /** Helper text displayed below the input */
    hint?: ReactNode;
}

export interface TextFieldProps
    extends BaseProps,
        AriaTextFieldProps,
        Pick<InputBaseProps, 'size' | 'wrapperClassName' | 'inputClassName' | 'tooltipClassName'> {
    ref?: Ref<HTMLDivElement>;
}

export const TextField = ({ className, ...props }: TextFieldProps) => {
    return (
        <TextFieldContext.Provider value={props}>
            <AriaTextField
                {...props}
                data-input-wrapper
                className={(state) =>
                    cx(
                        SEMANTIC_NAME_TEXT_FIELD,
                        'group flex h-max w-full flex-col items-start justify-start gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            />
        </TextFieldContext.Provider>
    );
};

TextField.displayName = SEMANTIC_NAME_TEXT_FIELD;

export interface InputProps extends InputBaseProps, BaseProps {
    /** Whether to hide required indicator from label */
    hideRequiredIndicator?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            size = 'sm',
            placeholder,
            iconLeading,
            iconTrailing,
            label,
            hint,
            shortcut,
            hideRequiredIndicator,
            className,
            groupRef,
            tooltip,
            inputClassName,
            wrapperClassName,
            tooltipClassName,
            ...props
        },
        ref,
    ) => {
        return (
            <TextField aria-label={!label ? placeholder : undefined} {...props} className={className}>
                {({ isRequired, isInvalid }) => (
                    <>
                        {label && (
                            <Label size={size} isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}>
                                {label}
                            </Label>
                        )}

                        <InputBase
                            {...{
                                ref,
                                groupRef,
                                size,
                                placeholder,
                                iconLeading,
                                iconTrailing,
                                shortcut,
                                inputClassName,
                                wrapperClassName,
                                tooltipClassName,
                                tooltip,
                            }}
                        />

                        {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
                    </>
                )}
            </TextField>
        );
    },
);

Input.displayName = SEMANTIC_NAME;
